import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Users, Phone, Mail, TrendingUp, ArrowRight, Search, Sparkles,
  Flame, Target, Trophy, Rocket, ChevronRight, Activity,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const CHALLENGE_DURATION_DAYS = 90;

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_leads: 0, leads_contacted: 0, replies_received: 0,
    follow_ups_due: 0, new_leads_today: 0, conversion_rate: 0,
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { bootstrap(); }, []);

  const bootstrap = async () => {
    try {
      const [statsRes, leadsRes, chRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/dashboard/recent-leads?limit=5`),
        axios.get(`${API_URL}/challenge/active`).catch(() => ({ data: { challenge: null } })),
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.leads || []);
      setChallenge(chRes.data?.challenge || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const todayObj = challenge ? challenge.days?.find(d => d.day === challenge.progress?.current_day) : null;
  const progress = challenge?.progress || {};

  const toggleTask = async (day, taskId, completed) => {
    setChallenge(prev => {
      if (!prev) return prev;
      const next = { ...prev, days: prev.days.map(d => ({ ...d, tasks: d.tasks.map(t => ({ ...t })) })) };
      const dObj = next.days.find(d => d.day === day);
      const tObj = dObj?.tasks.find(t => t.id === taskId);
      if (tObj) tObj.completed = completed;
      return next;
    });
    try {
      const { data } = await axios.post(`${API_URL}/challenge/task/toggle`, { day, task_id: taskId, completed });
      setChallenge(data.challenge);
    } catch (e) {
      bootstrap();
    }
  };

  // Mock weekly chart
  const chartData = [
    { name: 'Mon', leads: 4, contacted: 3 },
    { name: 'Tue', leads: 6, contacted: 4 },
    { name: 'Wed', leads: 8, contacted: 5 },
    { name: 'Thu', leads: 5, contacted: 4 },
    { name: 'Fri', leads: 9, contacted: 6 },
    { name: 'Sat', leads: 3, contacted: 2 },
    { name: 'Sun', leads: 2, contacted: 1 },
  ];

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  })();

  const firstName = (stats.user_name || '').split(' ')[0] || 'there';

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-6 lg:space-y-8 max-w-[1500px] mx-auto" data-testid="dashboard-content">

        {/* ============ HERO STRIP ============ */}
        <section className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
          <div className="absolute inset-0 hero-mesh opacity-90" />
          <div className="relative p-5 sm:p-7 lg:p-8 grid lg:grid-cols-[1fr_auto] gap-6 items-center">
            <div className="min-w-0">
              <p className="text-[11px] font-bold tracking-[0.18em] text-violet-700 uppercase mb-2">Your Client Pipeline</p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-slate-900 leading-tight">
                {greeting}, <span className="gradient-text">{firstName}</span>
              </h1>
              <p className="text-slate-600 mt-2 max-w-xl">
                {challenge
                  ? `You're on Day ${progress.current_day || 1}/${progress.duration_days || CHALLENGE_DURATION_DAYS} of your challenge. ${progress.streak ? `🔥 ${progress.streak}-day streak.` : 'Today is the day to start a streak.'}`
                  : 'Start your 90-Day Client Acquisition Challenge to see daily targets here.'}
              </p>

              {challenge ? (
                <div className="mt-5">
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                    <span>Day {progress.current_day || 1} of {progress.duration_days || CHALLENGE_DURATION_DAYS}</span>
                    <span>{progress.completion_pct || 0}% complete</span>
                  </div>
                  <div className="progress-track h-2.5">
                    <div className="progress-fill" style={{ width: `${progress.completion_pct || 0}%` }} />
                  </div>
                </div>
              ) : (
                <Link to="/challenge" className="inline-block mt-5">
                  <button className="btn-primary" data-testid="start-challenge-cta">
                    <Rocket className="w-4 h-4" /> Start 90-Day Challenge
                  </button>
                </Link>
              )}
            </div>

            {/* Next Step CTA */}
            <NextStepCard challenge={challenge} progress={progress} todayObj={todayObj} />
          </div>
        </section>

        {/* ============ KPI ROW ============ */}
        <section className="-mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex sm:grid gap-4 sm:grid-cols-2 lg:grid-cols-4 overflow-x-auto sm:overflow-visible snap-x sm:snap-none">
            <KpiCard testid="kpi-clients" icon={Users} label="Potential Clients" value={stats.total_leads}
                     hint={`+${stats.new_leads_today || 0} today`} accent="violet" />
            <KpiCard testid="kpi-contacted" icon={Phone} label="Contacted" value={stats.leads_contacted}
                     hint="Outreach sent" accent="blue" />
            <KpiCard testid="kpi-replies" icon={Mail} label="Replies" value={stats.replies_received}
                     hint={`${stats.conversion_rate || 0}% reply rate`} accent="emerald" />
            <KpiCard testid="kpi-followups" icon={TrendingUp} label="Deals in Progress" value={stats.follow_ups_due}
                     hint="Follow-ups due" accent="amber" />
          </div>
        </section>

        {/* ============ TODAY'S TASKS + ACTIVITY ============ */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Today's Tasks (challenge) */}
          <div className="lg:col-span-2 card-base p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <span className="icon-halo">
                  <Flame className="w-4 h-4 text-violet-700" strokeWidth={2.5} />
                </span>
                <div>
                  <h2 className="text-base font-heading font-bold text-slate-900">Today's Mission</h2>
                  <p className="text-xs text-slate-500">{challenge ? `Day ${progress.current_day} · ${todayObj?.focus || ''}` : 'No active challenge'}</p>
                </div>
              </div>
              <Link to="/challenge" className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1">
                Open <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {challenge && todayObj ? (
              <div className="space-y-2">
                {todayObj.tasks.slice(0, 5).map((t) => (
                  <label
                    key={t.id}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${
                      t.completed ? 'bg-emerald-50/60 border-emerald-100' : 'bg-slate-50/60 border-slate-200 hover:bg-white hover:border-slate-300'
                    }`}
                    data-testid={`dash-task-${t.id}`}
                  >
                    <Checkbox
                      checked={!!t.completed}
                      onCheckedChange={(v) => toggleTask(todayObj.day, t.id, !!v)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${t.completed ? 'line-through text-slate-500' : 'text-slate-900'}`}>{t.label}</p>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className="badge-info">{(t.category || '').replace('_', ' ')}</span>
                        {t.target_count > 0 && (
                          <span className="badge-neutral">Target: {t.target_count}</span>
                        )}
                        {t.is_critical && <span className="badge-danger">Critical</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <EmptyChallenge />
            )}
          </div>

          {/* Activity stream + Quick actions */}
          <div className="space-y-6">
            <div className="card-base p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2.5">
                  <span className="icon-halo"><Activity className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
                  <h2 className="text-base font-heading font-bold text-slate-900">Weekly Activity</h2>
                </div>
              </div>
              <div className="h-40 min-h-[160px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 0, bottom: 0, left: -22 }}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.45}/>
                        <stop offset="100%" stopColor="#7C3AED" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35}/>
                        <stop offset="100%" stopColor="#2563EB" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, fontSize: 12 }} />
                    <Area type="monotone" dataKey="leads" stroke="#7C3AED" strokeWidth={2} fill="url(#g1)" />
                    <Area type="monotone" dataKey="contacted" stroke="#2563EB" strokeWidth={2} fill="url(#g2)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card-base p-5 sm:p-6">
              <h2 className="text-base font-heading font-bold text-slate-900 mb-3">Quick actions</h2>
              <div className="space-y-2">
                <QuickAction to="/search"     icon={Search}    title="Find clients"      subtitle="Search by niche + state" />
                <QuickAction to="/outreach"   icon={Mail}      title="Send outreach"     subtitle="AI scripts + follow-ups" />
                <QuickAction to="/marketing"  icon={Sparkles}  title="Build a campaign"  subtitle="Ad creatives + playbooks" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ RECENT LEADS ============ */}
        <section className="card-base p-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-heading font-bold text-slate-900">Recent potential clients</h2>
              <p className="text-xs text-slate-500">Most recent saves</p>
            </div>
            <Link to="/leads" className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {recentLeads.length > 0 ? (
            <div className="-mx-5 sm:-mx-6 overflow-x-auto">
              <table className="w-full">
                <thead className="table-head">
                  <tr>
                    <th className="table-th">Business</th>
                    <th className="table-th hidden md:table-cell">Niche</th>
                    <th className="table-th hidden sm:table-cell">Location</th>
                    <th className="table-th">Status</th>
                    <th className="table-th text-right pr-6">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map((lead) => (
                    <tr key={lead.id} className="table-row">
                      <td className="table-td">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-100 to-blue-100 flex items-center justify-center text-violet-700 font-bold text-xs flex-shrink-0">
                            {(lead.business_name || 'L').slice(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 truncate">{lead.business_name}</div>
                            {lead.email && <div className="text-xs text-slate-500 truncate">{lead.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="table-td hidden md:table-cell text-slate-600">{lead.niche}</td>
                      <td className="table-td hidden sm:table-cell text-slate-600">{lead.city}{lead.state ? `, ${lead.state}` : ''}</td>
                      <td className="table-td"><StatusBadge status={lead.status} /></td>
                      <td className="table-td text-right pr-6">
                        <Link to={`/leads/${lead.id}`} className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1" data-testid={`view-lead-${lead.id}`}>
                          Open <ChevronRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center mb-4 shadow-glow">
                <Target className="w-6 h-6 text-white" />
              </div>
              <p className="font-heading font-bold text-slate-900 text-lg">No clients saved yet</p>
              <p className="text-sm text-slate-500 max-w-sm mt-1.5">
                Pick a niche, find businesses ready for your service, save them here, then start outreach.
              </p>
              <Link to="/search" className="mt-5">
                <button className="btn-primary" data-testid="empty-search-btn">
                  <Search className="w-4 h-4" /> Find your first client
                </button>
              </Link>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

// =================== Subcomponents ===================

const NextStepCard = ({ challenge, progress, todayObj }) => {
  const remaining = todayObj ? todayObj.tasks.filter(t => !t.completed).length : 0;
  return (
    <div className="card-glass p-5 lg:max-w-sm flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="icon-halo"><Trophy className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
        <p className="text-[11px] font-bold tracking-[0.16em] text-violet-700 uppercase">Next Step</p>
      </div>
      {challenge && todayObj ? (
        <>
          <p className="text-sm text-slate-900 font-semibold leading-snug">
            {remaining > 0
              ? `Knock out ${remaining} task${remaining > 1 ? 's' : ''} on today's mission.`
              : `🎉 You crushed today. Day ${progress.current_day}/${progress.duration_days || CHALLENGE_DURATION_DAYS} done.`}
          </p>
          <Link to="/challenge">
            <button className="btn-primary w-full justify-center" data-testid="next-step-cta">
              Open today's mission <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-900 font-semibold leading-snug">
            Set up your 90-Day Challenge to get a personalized daily plan.
          </p>
          <Link to="/challenge">
            <button className="btn-primary w-full justify-center" data-testid="next-step-cta">
              <Rocket className="w-4 h-4" /> Start now
            </button>
          </Link>
        </>
      )}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, hint, accent, testid }) => {
  const accents = {
    violet:  'from-violet-500 to-violet-600',
    blue:    'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber:   'from-amber-500 to-orange-500',
  };
  return (
    <div className="card-base card-hover p-5 min-w-[220px] sm:min-w-0 snap-start" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold tracking-[0.16em] text-slate-500 uppercase">{label}</p>
        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>
      <p className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-3 tabular-nums tracking-tight">{value ?? 0}</p>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </div>
  );
};

const QuickAction = ({ to, icon: Icon, title, subtitle }) => (
  <Link to={to} className="group flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 transition-all">
    <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-100 to-blue-100 group-hover:from-violet-500 group-hover:to-blue-500 flex items-center justify-center text-violet-700 group-hover:text-white transition-colors">
      <Icon className="w-4 h-4" strokeWidth={2.5} />
    </span>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-xs text-slate-500">{subtitle}</p>
    </div>
    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-violet-700" />
  </Link>
);

const StatusBadge = ({ status }) => {
  const map = {
    new:        { c: 'badge-info',    l: 'New' },
    contacted:  { c: 'badge-warning', l: 'Contacted' },
    follow_up:  { c: 'badge-warning', l: 'Follow-up' },
    replied:    { c: 'badge-success', l: 'Replied' },
    closed:     { c: 'badge-success', l: 'Closed' },
  };
  const v = map[status] || map.new;
  return <span className={v.c}>{v.l}</span>;
};

const EmptyChallenge = () => (
  <div className="empty-state">
    <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center mb-3 shadow-glow">
      <Flame className="w-5 h-5 text-white" />
    </span>
    <p className="font-heading font-bold text-slate-900">No active challenge</p>
    <p className="text-sm text-slate-500 max-w-xs mt-1.5">Get a personalized 90-day plan to land your first paying client.</p>
    <Link to="/challenge" className="mt-4">
      <button className="btn-primary" data-testid="empty-challenge-btn">
        <Rocket className="w-4 h-4" /> Start the challenge
      </button>
    </Link>
  </div>
);

export default Dashboard;
